import { Link } from 'react-router-dom';
import type { Episode, Season } from '../types';
import EpisodeCard from './EpisodeCard';

interface RailProps {
  episodes: Episode[];
  title: string;
  subtitle?: string | null;
  showProgress?: boolean;
  /** "View all" target; default the episodes' season, null = no link. */
  viewAllTo?: string | null;
}

/**
 * A horizontally scrolling row of episodes, like a streaming home row.
 * Scroll is native (snap + momentum) so it feels right on touch devices.
 */
export default function Rail({ episodes, title, subtitle, showProgress, viewAllTo }: RailProps) {
  if (episodes.length === 0) return null;

  return (
    <section className="mb-10 animate-slide-up">
      <div className="mb-3 flex items-baseline gap-3 px-4 sm:px-6 lg:px-10">
        <h2 className="text-lg font-bold tracking-tight sm:text-xl">{title}</h2>
        {subtitle && <span className="truncate text-xs text-white/40">{subtitle}</span>}
        {viewAllTo !== null && (
        <div className="ml-auto hidden shrink-0 sm:block">
          <Link
            to={viewAllTo ?? `/season/${episodes[0].year}`}
            className="text-xs font-medium text-white/50 transition hover:text-white"
          >
            View all ›
          </Link>
        </div>
        )}
      </div>

      <div className="flex snap-x snap-mandatory gap-3 overflow-x-auto px-4 pb-2 sm:gap-4 sm:px-6 lg:px-10">
        {episodes.map((ep, i) => (
          <div key={ep.id} className="snap-start">
            <EpisodeCard episode={ep} index={i} showProgress={showProgress} />
          </div>
        ))}
        {/* Trailing spacer so the last card is not flush against the edge. */}
        <div className="w-1 shrink-0" aria-hidden />
      </div>
    </section>
  );
}

/** Large hero banner for the most recent episode. */
export function Hero({ episode, stats }: { episode: Episode | null; stats: { media: number; episodes: number; seasons: number } }) {
  if (!episode) return null;

  return (
    <section className="relative mb-12 h-[62vh] min-h-[420px] w-full overflow-hidden">
      {/* Backdrop: the cover image, blurred and darkened behind the crisp panel. */}
      {episode.backdropUrl && (
        <img
          src={episode.backdropUrl}
          alt=""
          className="absolute inset-0 h-full w-full scale-110 object-cover blur-xl brightness-[0.45]"
        />
      )}
      <div className="absolute inset-0 bg-gradient-to-r from-ink-950 via-ink-950/85 to-ink-950/20" />
      <div className="absolute inset-0 bg-gradient-to-t from-ink-950 via-transparent to-transparent" />

      <div className="relative mx-auto flex h-full max-w-[1600px] flex-col justify-end px-4 pb-12 sm:px-6 lg:px-10 lg:pb-16">
        <div className="max-w-xl animate-fade-in">
          <div className="mb-3 text-[11px] font-semibold tracking-[0.2em] text-white/50 uppercase">
            Season {episode.year} · {episode.mediaCount} memories
          </div>

          <h1 className="mb-1 text-4xl leading-[1.05] font-extrabold tracking-tight sm:text-6xl">
            {episode.title}
          </h1>

          {episode.description && (
            <p className="mt-4 max-w-md text-sm leading-relaxed text-white/65 sm:text-base">
              {episode.description}
            </p>
          )}

          <div className="mt-7 flex flex-wrap items-center gap-3">
            <Link
              to={`/play/${episode.id}`}
              className="focus-ring flex items-center gap-2.5 rounded-md bg-white px-6 py-3 text-sm font-bold text-black transition hover:bg-white/85"
            >
              <svg viewBox="0 0 24 24" className="h-5 w-5" fill="currentColor">
                <path d="M8 5v14l11-7z" />
              </svg>
              Play Your Story
            </Link>
            <Link
              to={`/episode/${episode.id}`}
              className="focus-ring rounded-md bg-white/15 px-6 py-3 text-sm font-semibold text-white backdrop-blur transition hover:bg-white/25"
            >
              More Info
            </Link>
          </div>

          <div className="mt-8 flex gap-6 text-xs text-white/40">
            <span>{stats.media} memories</span>
            <span>{stats.episodes} stories</span>
            <span>{stats.seasons} seasons</span>
          </div>
        </div>
      </div>
    </section>
  );
}

export type { Season };